import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

import type { WordDiffSide } from "./review-word-diff"
import type { Node } from "@tiptap/pm/model"
import {
	changedWordRanges,
	layoutSegments,
	rangesWithin,
} from "./review-word-diff"

export interface WordDiffHighlightOptions {
	otherText: string
	side: WordDiffSide
}

// Decorations rather than marks: the highlight never enters the document, so
// the HTML a reviewer saves back carries no diff spans.
export function wordDiffDecorations(
	doc: Node,
	otherText: string,
	side: WordDiffSide,
): DecorationSet {
	const positions: number[] = []
	const parts: { text: string; blockStart: boolean }[] = []
	let previousEnd = -1
	doc.descendants((node, pos) => {
		if (!node.isText || !node.text) return
		// Adjacent text runs share a block; any gap means a node boundary sits
		// between them.
		parts.push({ text: node.text, blockStart: pos !== previousEnd })
		positions.push(pos)
		previousEnd = pos + node.text.length
	})

	const { fullText, segments } = layoutSegments(parts)
	const ranges = changedWordRanges(fullText, otherText)
	if (ranges.length === 0) return DecorationSet.empty

	const decorations: Decoration[] = []
	segments.forEach((segment, index) => {
		const pos = positions[index]
		if (pos === undefined) return
		for (const { start, end } of rangesWithin(ranges, segment)) {
			decorations.push(
				Decoration.inline(pos + start, pos + end, { "data-diff-word": side }),
			)
		}
	})
	return DecorationSet.create(doc, decorations)
}

export const WordDiffHighlight = Extension.create<WordDiffHighlightOptions>({
	name: "wordDiffHighlight",

	addOptions() {
		return { otherText: "", side: "insert" }
	},

	addProseMirrorPlugins() {
		const { otherText, side } = this.options
		return [
			new Plugin<DecorationSet>({
				key: new PluginKey("wordDiffHighlight"),
				state: {
					init: (_config, state) =>
						wordDiffDecorations(state.doc, otherText, side),
					apply: (tr, previous) =>
						tr.docChanged
							? wordDiffDecorations(tr.doc, otherText, side)
							: previous,
				},
				props: {
					decorations(state) {
						return this.getState(state)
					},
				},
			}),
		]
	},
})
